import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from '../../axiosConfig';
import { FiSearch, FiCheck } from 'react-icons/fi';
import { CustomDatePicker } from '../commons/datepicker';
import { useToast } from '../commons/toast/ToastContext';
import '../../styles/AssignReportModal.css';

const AssignReportModal = ({ isOpen, onClose, dogId, templates = [], onAssigned, schoolId, preselectedTemplateId }) => {
    const { t } = useTranslation('reports');
    const toast = useToast();
    const [formData, setFormData] = useState({
        reportTemplateId: preselectedTemplateId || '',
        startDate: '',
        endDate: '',
        recurrence: 'ONCE'
    });
    const [selectedDogIds, setSelectedDogIds] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [dogs, setDogs] = useState([]);
    const [filteredDogs, setFilteredDogs] = useState([]);
    const [dogsLoading, setDogsLoading] = useState(false);
    const [activeAssignments, setActiveAssignments] = useState({});

    const showDogSelector = !dogId;

    // Fetch dogs when opened from TemplatesManagement (schoolId provided, no dogId)
    useEffect(() => {
        if (isOpen && schoolId && !dogId) {
            fetchDogs();
        }
    }, [isOpen, schoolId, dogId]);

    // Reset form when modal opens
    useEffect(() => {
        if (isOpen) {
            setFormData({
                reportTemplateId: preselectedTemplateId || '',
                startDate: '',
                endDate: '',
                recurrence: 'ONCE'
            });
            setSelectedDogIds([]);
            setSearchTerm('');
            setActiveAssignments({});
        }
    }, [isOpen, preselectedTemplateId, dogId]);

    const fetchDogs = async () => {
        try {
            setDogsLoading(true);
            const response = await axios.get(`/api/v1/dog/list/${schoolId}/booking`);
            setDogs(response.data);
            setFilteredDogs(response.data);
        } catch (err) {
            console.error('Error fetching dogs:', err);
        } finally {
            setDogsLoading(false);
        }
    };

    // Fetch active assignments when template is selected
    const fetchActiveAssignments = useCallback(async (templateId) => {
        if (!templateId || !showDogSelector) {
            setActiveAssignments({});
            return;
        }
        try {
            const response = await axios.get(`/api/v1/reports/assignments/report/${templateId}`);
            const today = new Date().toISOString().split('T')[0];
            const activeDogIds = {};
            response.data.forEach(assignment => {
                const isActive = !assignment.endDate || assignment.endDate >= today;
                if (isActive) {
                    activeDogIds[assignment.dogId] = true;
                }
            });
            setActiveAssignments(activeDogIds);
        } catch (err) {
            console.error('Error fetching assignments:', err);
        }
    }, [showDogSelector]);

    // Fetch assignments when template changes
    useEffect(() => {
        if (isOpen && formData.reportTemplateId) {
            fetchActiveAssignments(formData.reportTemplateId);
        }
    }, [isOpen, formData.reportTemplateId, fetchActiveAssignments]);

    // Filter dogs when search term changes
    useEffect(() => {
        if (!searchTerm.trim()) {
            setFilteredDogs(dogs);
            return;
        }
        const searchTermLower = searchTerm.toLowerCase();
        const filtered = dogs.filter(dog =>
            dog.name.toLowerCase().includes(searchTermLower) ||
            (dog.breed && dog.breed.toLowerCase().includes(searchTermLower)) ||
            (dog.ownersName && dog.ownersName.toLowerCase().includes(searchTermLower))
        );
        setFilteredDogs(filtered);
    }, [searchTerm, dogs]);

    // Recurrence options
    const recurrenceOptions = [
        { value: 'ONCE', label: t('recurrence.once') },
        { value: 'DAILY', label: t('recurrence.daily') },
        { value: 'EVERY_2_DAYS', label: t('recurrence.every2Days') },
        { value: 'EVERY_3_DAYS', label: t('recurrence.every3Days') },
        { value: 'EVERY_4_DAYS', label: t('recurrence.every4Days') },
        { value: 'EVERY_5_DAYS', label: t('recurrence.every5Days') },
        { value: 'EVERY_6_DAYS', label: t('recurrence.every6Days') },
        { value: 'WEEKLY', label: t('recurrence.weekly') }
    ];

    const toggleDogSelection = (id) => {
        if (activeAssignments[id]) return;
        setSelectedDogIds(prev =>
            prev.includes(id) ? prev.filter(d => d !== id) : [...prev, id]
        );
    };

    const selectableDogs = filteredDogs.filter(dog => !activeAssignments[dog.id]);

    const handleSelectAll = () => {
        const allSelectableIds = selectableDogs.map(dog => dog.id);
        setSelectedDogIds(prev => {
            const currentSet = new Set(prev);
            const allAlreadySelected = allSelectableIds.every(id => currentSet.has(id));
            if (allAlreadySelected) {
                return prev.filter(id => !allSelectableIds.includes(id));
            }
            return [...new Set([...prev, ...allSelectableIds])];
        });
    };

    // Handle form submission
    const handleSubmit = async () => {
        const dogsToAssign = dogId ? [dogId] : selectedDogIds;
        if (!formData.reportTemplateId || !formData.startDate || dogsToAssign.length === 0) {
            return;
        }

        try {
            setLoading(true);
            const requests = dogsToAssign.map(id =>
                axios.post('/api/v1/reports/assignments', {
                    reportTemplateId: formData.reportTemplateId,
                    dogId: id,
                    startDate: formData.startDate,
                    endDate: formData.endDate || null,
                    recurrence: formData.recurrence
                })
            );
            await Promise.all(requests);
            toast.success(t('reportAssignedSuccess'));
            if (onAssigned) {
                onAssigned();
            }
            onClose();
        } catch (err) {
            console.error('Error assigning report:', err);
            toast.error(t('assignError'));
        } finally {
            setLoading(false);
        }
    };

    // Get current date in YYYY-MM-DD format for date input
    const getTodayDate = () => {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    if (!isOpen) return null;

    // Determine if we need to show template selector (not pre-selected)
    const showTemplateSelector = !preselectedTemplateId;
    const canSubmit = dogId
        ? formData.reportTemplateId && formData.startDate
        : formData.reportTemplateId && formData.startDate && selectedDogIds.length > 0;

    return (
        <div className="assign-modal-overlay" onClick={onClose}>
            <div className="assign-modal-content" onClick={e => e.stopPropagation()}>
                <div className="assign-modal-header">
                    <h2>{t('assignReport')}</h2>
                    <button className="assign-modal-close" onClick={onClose}>
                        &times;
                    </button>
                </div>

                <div className="assign-modal-body">
                    {/* Template selector - shown when not pre-selected */}
                    {showTemplateSelector && (
                        <div className="assign-form-group">
                            <label htmlFor="reportTemplate">{t('selectTemplate')}</label>
                            <select
                                id="reportTemplate"
                                className="assign-form-select"
                                value={formData.reportTemplateId}
                                onChange={(e) => setFormData({ ...formData, reportTemplateId: e.target.value })}
                            >
                                <option value="">{t('selectTemplatePlaceholder')}</option>
                                {templates.map(template => (
                                    <option key={template.id} value={template.id}>
                                        {template.title}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {/* Dog selector - shown when no dogId provided */}
                    {showDogSelector && (
                        <div className="assign-form-group">
                            <div className="assign-dog-selector-header">
                                <label>{t('dog')}</label>
                                {filteredDogs.length > 0 && (
                                    <div className="assign-selection-controls">
                                        <span className="assign-selected-count">
                                            {t('selectedCount', { selected: selectedDogIds.length, total: selectableDogs.length })}
                                        </span>
                                        <button
                                            type="button"
                                            className="assign-select-all-btn"
                                            onClick={handleSelectAll}
                                        >
                                            {selectableDogs.length > 0 && selectableDogs.every(d => selectedDogIds.includes(d.id))
                                                ? t('deselectAll')
                                                : t('selectAll')}
                                        </button>
                                    </div>
                                )}
                            </div>

                            <div className="assign-search-container">
                                <div className="assign-search-wrapper">
                                    <FiSearch className="assign-search-icon" />
                                    <input
                                        type="text"
                                        className="assign-search-input"
                                        placeholder={t('searchDogPlaceholder')}
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                            </div>

                            {dogsLoading ? (
                                <div className="assign-loading-dogs">{t('loading')}</div>
                            ) : (
                                <div className="assign-dog-list">
                                    {filteredDogs.map((dog) => {
                                        const isActive = activeAssignments[dog.id];
                                        const isSelected = selectedDogIds.includes(dog.id);
                                        return (
                                            <div
                                                key={dog.id}
                                                className={`assign-dog-card ${isSelected ? 'assign-dog-card--selected' : ''} ${isActive ? 'assign-dog-card--active' : ''}`}
                                                onClick={() => toggleDogSelection(dog.id)}
                                            >
                                                <div className="assign-dog-card-avatar">
                                                    {dog.name.charAt(0).toUpperCase()}
                                                </div>
                                                <div className="assign-dog-card-info">
                                                    <span className="assign-dog-card-name">{dog.name}</span>
                                                    {dog.breed && <span className="assign-dog-card-breed">{dog.breed}</span>}
                                                </div>
                                                {dog.ownersName && (
                                                    <span className="assign-dog-card-owner">{dog.ownersName}</span>
                                                )}
                                                {isActive && (
                                                    <span className="assign-dog-card-badge">{t('assigned')}</span>
                                                )}
                                                {isSelected && (
                                                    <div className="assign-dog-card-check">
                                                        <FiCheck />
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {filteredDogs.length === 0 && searchTerm && !dogsLoading && (
                                <div className="assign-no-results">
                                    {t('noDogsFound')}
                                </div>
                            )}
                        </div>
                    )}

                    <div className="assign-form-group">
                        <label htmlFor="startDate">{t('startDate')}</label>
                        <CustomDatePicker
                            id="startDate"
                            name="startDate"
                            min={getTodayDate()}
                            value={formData.startDate}
                            onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                        />
                    </div>

                    <div className="assign-form-group">
                        <label htmlFor="endDate">{t('endDateOptional')}</label>
                        <CustomDatePicker
                            id="endDate"
                            name="endDate"
                            min={getTodayDate()}
                            value={formData.endDate}
                            onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                        />
                    </div>

                    <div className="assign-form-group">
                        <label htmlFor="recurrence">{t('recurrenceLabel')}</label>
                        <select
                            id="recurrence"
                            className="assign-form-select"
                            value={formData.recurrence}
                            onChange={(e) => setFormData({ ...formData, recurrence: e.target.value })}
                        >
                            {recurrenceOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="assign-modal-footer">
                    <button
                        className="assign-cancel-btn"
                        onClick={onClose}
                        disabled={loading}
                    >
                        {t('cancel')}
                    </button>
                    <button
                        className="assign-submit-btn"
                        onClick={handleSubmit}
                        disabled={loading || !canSubmit}
                    >
                        {loading ? t('assigning') : t('assign')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AssignReportModal;
